import { auth } from "@/lib/firebase";

/**
 * API client.
 *
 * One place that knows how to talk to the backend: it attaches the Firebase ID
 * token, normalises error shapes into something the UI can render, and
 * recognises the one error that is not really an error — an expired session,
 * which needs a sign-out rather than a red banner.
 */

const BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

/** Thrown for any non-2xx response. Carries the field errors a form needs. */
export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details || null;
    // Field-level messages, keyed by form field name.
    this.fieldErrors = details && typeof details === "object" && !Array.isArray(details)
      ? details
      : null;
  }

  get isSessionExpired() {
    return this.code === "session_expired";
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isRateLimited() {
    return this.status === 429;
  }
}

/**
 * Subscribers notified when the server reports the session is over.
 * AuthContext registers one and signs the user out.
 */
const sessionExpiryHandlers = new Set();

export function onSessionExpired(handler) {
  sessionExpiryHandlers.add(handler);
  return () => sessionExpiryHandlers.delete(handler);
}

async function authHeader(required) {
  const user = auth.currentUser;
  if (!user) {
    if (required) throw new ApiError(401, "unauthenticated", "Please sign in to continue.");
    return {};
  }
  // getIdToken refreshes automatically when the current token is close to
  // expiry, so long editing sessions do not fail on save.
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function request(path, { method = "GET", body, auth: needsAuth = true, signal, raw = false } = {}) {
  const headers = { ...(await authHeader(needsAuth)) };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === "AbortError") throw err;
    throw new ApiError(
      0,
      "network",
      "Could not reach the server. Check your connection and try again."
    );
  }

  if (raw) {
    if (!response.ok) throw await toApiError(response);
    return response;
  }

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload && payload.error
      ? new ApiError(response.status, payload.error.code, payload.error.message, payload.error.details)
      : new ApiError(response.status, "unknown", `Request failed (${response.status}).`);

    if (error.isSessionExpired) {
      sessionExpiryHandlers.forEach((handler) => handler(error));
    }
    throw error;
  }

  return payload;
}

async function toApiError(response) {
  const payload = await response.json().catch(() => null);
  return payload && payload.error
    ? new ApiError(response.status, payload.error.code, payload.error.message, payload.error.details)
    : new ApiError(response.status, "unknown", `Request failed (${response.status}).`);
}

/** Build a query string, dropping empty and "all" values. */
export function qs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === "all") return;
    search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : "";
}

/* -------------------------------------------------------------------------- */
/* Public endpoints — no authentication                                        */
/* -------------------------------------------------------------------------- */

export const publicApi = {
  bootstrap: () => request("/public/bootstrap", { auth: false }),
  resolve: (path) => request(`/public/resolve${qs({ path })}`, { auth: false }),

  blogs: (params) => request(`/public/blogs${qs(params)}`, { auth: false }),
  blog: (slug) => request(`/public/blogs/${encodeURIComponent(slug)}`, { auth: false }),
  blogCategories: () => request("/public/blog-categories", { auth: false }),

  caseStudies: (params) => request(`/public/case-studies${qs(params)}`, { auth: false }),
  caseStudy: (slug) => request(`/public/case-studies/${encodeURIComponent(slug)}`, { auth: false }),

  testimonials: (params) => request(`/public/testimonials${qs(params)}`, { auth: false }),
  awards: () => request("/public/awards", { auth: false }),

  seo: (key) => request(`/public/seo/${encodeURIComponent(key)}`, { auth: false }),
  codeInjections: (path) => request(`/public/code-injections${qs({ path })}`, { auth: false }),

  submitEnquiry: (payload) =>
    request("/public/enquiries", { method: "POST", body: payload, auth: false }),

  // Property valuation. Both calls are proxied server-side — the upstream key is
  // billed per valuation and never reaches the browser.
  resolvePostalCode: (payload) =>
    request("/public/valuation/resolve", { method: "POST", body: payload, auth: false }),
  requestValuation: (payload) =>
    request("/public/valuation/estimate", { method: "POST", body: payload, auth: false }),
};

/* -------------------------------------------------------------------------- */
/* Authentication                                                              */
/* -------------------------------------------------------------------------- */

export const authApi = {
  login: (email, password) =>
    request("/auth/login", { method: "POST", body: { email, password }, auth: false }),
  forgotPassword: (email) =>
    request("/auth/forgot-password", { method: "POST", body: { email }, auth: false }),
  me: () => request("/auth/me"),
  myLoginActivity: () => request("/auth/me/login-activity"),
  changePassword: (currentPassword, newPassword) =>
    request("/auth/change-password", { method: "POST", body: { currentPassword, newPassword } }),
  logout: () => request("/auth/logout", { method: "POST" }),
};

/* -------------------------------------------------------------------------- */
/* Admin endpoints                                                             */
/* -------------------------------------------------------------------------- */

/** Standard CRUD surface, generated per resource to avoid twenty near-copies. */
function resource(base) {
  return {
    list: (params) => request(`${base}${qs(params)}`),
    get: (id) => request(`${base}/${encodeURIComponent(id)}`),
    preview: (id) => request(`${base}/${encodeURIComponent(id)}/preview`),
    create: (body) => request(base, { method: "POST", body }),
    update: (id, body) => request(`${base}/${encodeURIComponent(id)}`, { method: "PATCH", body }),
    remove: (id, permanent = false) =>
      request(`${base}/${encodeURIComponent(id)}${qs({ permanent: permanent || undefined })}`, {
        method: "DELETE",
      }),
    restore: (id) => request(`${base}/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    publish: (id) => request(`${base}/${encodeURIComponent(id)}/publish`, { method: "POST" }),
    unpublish: (id) => request(`${base}/${encodeURIComponent(id)}/unpublish`, { method: "POST" }),
    archive: (id) => request(`${base}/${encodeURIComponent(id)}/archive`, { method: "POST" }),
    schedule: (id, scheduledFor) =>
      request(`${base}/${encodeURIComponent(id)}/schedule`, { method: "POST", body: { scheduledFor } }),
    duplicate: (id) => request(`${base}/${encodeURIComponent(id)}/duplicate`, { method: "POST" }),
    reorder: (order) => request(`${base}/reorder`, { method: "POST", body: { order } }),
  };
}

export const adminApi = {
  meta: () => request("/admin/meta"),
  dashboard: () => request("/admin/dashboard/summary"),

  blogs: resource("/admin/blogs"),
  caseStudies: resource("/admin/case-studies"),
  testimonials: resource("/admin/testimonials"),
  awards: resource("/admin/awards"),

  enquiries: {
    list: (params) => request(`/admin/enquiries${qs(params)}`),
    get: (id) => request(`/admin/enquiries/${encodeURIComponent(id)}`),
    stats: () => request("/admin/enquiries/stats"),
    update: (id, body) => request(`/admin/enquiries/${encodeURIComponent(id)}`, { method: "PATCH", body }),
    markSpam: (id) => request(`/admin/enquiries/${encodeURIComponent(id)}/spam`, { method: "POST" }),
    toggleArchive: (id) => request(`/admin/enquiries/${encodeURIComponent(id)}/archive`, { method: "POST" }),
    remove: (id, permanent = false) =>
      request(`/admin/enquiries/${encodeURIComponent(id)}${qs({ permanent: permanent || undefined })}`, {
        method: "DELETE",
      }),
    restore: (id) => request(`/admin/enquiries/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    addNote: (id, body) =>
      request(`/admin/enquiries/${encodeURIComponent(id)}/notes`, { method: "POST", body: { body } }),
    deleteNote: (id, noteId) =>
      request(`/admin/enquiries/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, {
        method: "DELETE",
      }),
    exportUrl: (params) => `${BASE}/admin/enquiries/export.csv${qs(params)}`,
    // The CSV download needs the auth header, so it cannot be a plain <a href>.
    // Fetch it, then hand the browser a blob URL.
    download: async (params) => {
      const response = await request(`/admin/enquiries/export.csv${qs(params)}`, { raw: true });
      return response.blob();
    },
  },

  seo: {
    pages: () => request("/admin/seo/pages"),
    page: (key) => request(`/admin/seo/pages/${encodeURIComponent(key)}`),
    savePage: (key, body) =>
      request(`/admin/seo/pages/${encodeURIComponent(key)}`, { method: "PUT", body }),
    checkSlug: (params) => request(`/admin/seo/slug-check${qs(params)}`),
    audit: () => request("/admin/seo/audit"),
  },

  redirects: {
    list: (params) => request(`/admin/redirects${qs(params)}`),
    create: (body) => request("/admin/redirects", { method: "POST", body }),
    update: (id, body) => request(`/admin/redirects/${encodeURIComponent(id)}`, { method: "PATCH", body }),
    remove: (id) => request(`/admin/redirects/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  codeInjection: {
    list: (params) => request(`/admin/code-injection${qs(params)}`),
    get: (id) => request(`/admin/code-injection/${encodeURIComponent(id)}`),
    affectedPages: (id) => request(`/admin/code-injection/${encodeURIComponent(id)}/affected-pages`),
    create: (body) => request("/admin/code-injection", { method: "POST", body }),
    update: (id, body) =>
      request(`/admin/code-injection/${encodeURIComponent(id)}`, { method: "PATCH", body }),
    activate: (id, confirm) =>
      request(`/admin/code-injection/${encodeURIComponent(id)}/activate`, {
        method: "POST",
        body: { confirm: confirm === true },
      }),
    deactivate: (id) =>
      request(`/admin/code-injection/${encodeURIComponent(id)}/deactivate`, { method: "POST" }),
    restoreVersion: (id, versionId) =>
      request(
        `/admin/code-injection/${encodeURIComponent(id)}/restore/${encodeURIComponent(versionId)}`,
        { method: "POST" }
      ),
    remove: (id) => request(`/admin/code-injection/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  admins: {
    list: (params) => request(`/admin/admins${qs(params)}`),
    get: (uid) => request(`/admin/admins/${encodeURIComponent(uid)}`),
    create: (body) => request("/admin/admins", { method: "POST", body }),
    update: (uid, body) => request(`/admin/admins/${encodeURIComponent(uid)}`, { method: "PATCH", body }),
    sendReset: (uid) => request(`/admin/admins/${encodeURIComponent(uid)}/send-reset`, { method: "POST" }),
    unlock: (uid) => request(`/admin/admins/${encodeURIComponent(uid)}/unlock`, { method: "POST" }),
    remove: (uid) => request(`/admin/admins/${encodeURIComponent(uid)}`, { method: "DELETE" }),
  },

  settings: {
    all: () => request("/admin/settings"),
    save: (section, body) => request(`/admin/settings/${section}`, { method: "PUT", body }),
    testEmail: (to) => request("/admin/settings/test-email", { method: "POST", body: { to } }),
  },

  activity: (params) => request(`/admin/activity${qs(params)}`),

  media: {
    list: () => request("/admin/media"),
    remove: (path) => request(`/admin/media${qs({ path })}`, { method: "DELETE" }),
  },
};

export { request, BASE as API_BASE };
